import React, { FC, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  VStack,
  Text
} from '@chakra-ui/react'

interface MobileRecordingWarningProps {
  isOpen: boolean
  onClose: () => void
}

export const MobileRecordingWarning: FC<MobileRecordingWarningProps> = ({
  isOpen,
  onClose
}) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose()
      }, 12000)

      return () => clearTimeout(timer)
    }
  }, [isOpen, onClose])
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay />
      <ModalContent borderRadius="none" zIndex="modal">
        <ModalHeader
          textAlign="center"
          fontWeight="bold"
          fontSize="20px"
          borderBottom="1px solid #E4E5E6"
        >
          Before you begin
        </ModalHeader>
        <ModalCloseButton pt={2} />

        <ModalBody
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <VStack spacing={6} align="center" mt={0} mb={16} textAlign="center">
            <VStack align="center">
              <svg
                width="47"
                height="54"
                viewBox="0 0 47 54"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.8333 16.3333H13.5V13.6666C13.5 12.911 13.2444 12.2777 12.7333 11.7666C12.2222 11.2555 11.5889 10.9999 10.8333 10.9999C10.0777 10.9999 9.44441 11.2555 8.9333 11.7666C8.42219 12.2777 8.16663 12.911 8.16663 13.6666C8.16663 14.4221 8.42219 15.0555 8.9333 15.5666C9.44441 16.0777 10.0777 16.3333 10.8333 16.3333ZM22.8333 16.3333C23.5889 16.3333 24.2222 16.0777 24.7333 15.5666C25.2444 15.0555 25.5 14.4221 25.5 13.6666C25.5 12.911 25.2444 12.2777 24.7333 11.7666C24.2222 11.2555 23.5889 10.9999 22.8333 10.9999C22.0777 10.9999 21.4444 11.2555 20.9333 11.7666C20.4222 12.2777 20.1666 12.911 20.1666 13.6666V16.3333H22.8333ZM21.4333 53.6666C20.1889 53.6666 19.0222 53.3999 17.9333 52.8666C16.8444 52.3333 15.9222 51.5777 15.1666 50.5999L0.633301 32.1333L1.89997 30.7999C2.78886 29.8666 3.85552 29.311 5.09997 29.1333C6.34441 28.9555 7.49997 29.1999 8.56663 29.8666L13.5 32.8666V21.6666H10.8333C8.61108 21.6666 6.72219 20.8888 5.16663 19.3333C3.61108 17.7777 2.8333 15.8888 2.8333 13.6666C2.8333 11.4444 3.61108 9.55547 5.16663 7.99992C6.72219 6.44436 8.61108 5.66659 10.8333 5.66659C11.3222 5.66659 11.7777 5.71103 12.2 5.79992C12.6222 5.88881 13.0555 5.99992 13.5 6.13325V2.99992C13.5 2.24436 13.7555 1.61103 14.2666 1.09992C14.7777 0.588808 15.4111 0.333252 16.1666 0.333252C16.9222 0.333252 17.5666 0.588808 18.1 1.09992C18.6333 1.61103 18.9 2.24436 18.9 2.99992V6.73325C19.5222 6.3777 20.1555 6.11103 20.8 5.93325C21.4444 5.75547 22.1222 5.66659 22.8333 5.66659C25.0555 5.66659 26.9444 6.44436 28.5 7.99992C30.0555 9.55547 30.8333 11.4444 30.8333 13.6666C30.8333 15.8888 30.0555 17.7777 28.5 19.3333C26.9444 20.8888 25.0555 21.6666 22.8333 21.6666H18.9V42.4666L12.4333 38.4666L19.3666 47.3333C19.6333 47.6444 19.9444 47.8888 20.3 48.0666C20.6555 48.2444 21.0333 48.3333 21.4333 48.3333H36.1666C37.6333 48.3333 38.8889 47.811 39.9333 46.7666C40.9777 45.7221 41.5 44.4666 41.5 42.9999V32.3333C41.5 31.5777 41.2444 30.9444 40.7333 30.4333C40.2222 29.9221 39.5889 29.6666 38.8333 29.6666H24.2333V24.3333H38.8333C41.0555 24.3333 42.9444 25.111 44.5 26.6666C46.0555 28.2221 46.8333 30.111 46.8333 32.3333V42.9999C46.8333 45.9333 45.7889 48.4444 43.7 50.5333C41.6111 52.6221 39.1 53.6666 36.1666 53.6666H21.4333Z"
                  fill="#2D54E8"
                />
              </svg>
              <Text fontSize="24px" fontWeight="semibold" color="blue.500">
                Stay on this page while recording!
              </Text>
            </VStack>

            <Text color="#000000">
              Changing apps, letting your phone go to sleep, or opening other
              tabs can cause recording issues. For optimal audio quality, please
              stay on this page while recording a session and turn off any
              auto-sleep features on your device.
            </Text>
          </VStack>

          <Button
            colorScheme="blue"
            size="lg"
            mt="auto"
            position="absolute"
            left={4}
            right={4}
            bottom={4}
            onClick={onClose}
          >
            Got it
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
